import React from 'react';
import { Dumbbell, Download, Calculator, Sparkles, BookOpen, Users } from 'lucide-react';

interface NavBarProps {
  onOpenCalculators: () => void;
  onOpenFatAI?: () => void;
  onOpenAuth: (mode: 'login' | 'register') => void;
  canShowIOSInstall: boolean;
  openGuide: () => void;
}

/**
 * NavBar — memoized to avoid re-renders from parent state changes on the
 * landing page. The container also uses `isolation` + `contain: paint` and
 * a solid (near-opaque) background instead of `backdrop-blur-xl` so that
 * hover-triggered link repaints don't force the browser to re-sample the
 * blurred layer under the nav (that was costing ~168ms per pointer event
 * and driving INP > 500ms).
 */
function NavBarImpl({ onOpenCalculators, onOpenFatAI, onOpenAuth, canShowIOSInstall, openGuide }: NavBarProps) {
  return (
    <nav
      className="fixed top-0 w-full bg-[#0a0e14]/95 z-50"
      style={{
        paddingTop: 'env(safe-area-inset-top, 0px)',
        isolation: 'isolate',
        contain: 'paint',
        transform: 'translateZ(0)',
      }}
    >
      <div className="flex justify-between items-center max-w-7xl mx-auto px-6 sm:px-8 py-4">
        <button
          type="button"
          onClick={() => {
            if (window.location.hash) {
              window.history.replaceState(null, '', window.location.pathname + window.location.search);
            }
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          aria-label="Back to top"
          data-testid="nav-logo-top-btn"
          className="text-xl sm:text-2xl font-bold tracking-tighter font-headline truncate bg-transparent border-0 p-0 cursor-pointer hover:opacity-80 transition-opacity"
        >
          <span className="text-[#cafd00]">Fitness</span><span className="text-[#f1f3fc]">&</span><span className="text-[#00f1fe]">me</span>
        </button>
        <div className="hidden md:flex gap-8 items-center">
          <a href="#features" className="text-[#cafd00] font-bold border-b-2 border-[#cafd00] pb-1 font-headline tracking-tight text-sm">Features</a>
          <a href="#screenshots" className="text-slate-400 font-medium hover:text-[#f1f3fc] transition-colors font-headline tracking-tight text-sm">App</a>
          <a href="#pricing" className="text-slate-400 font-medium hover:text-[#f1f3fc] transition-colors font-headline tracking-tight text-sm">Pricing</a>
          <a
            href="/exercises.html"
            className="text-slate-400 font-medium hover:text-[#f1f3fc] transition-colors font-headline tracking-tight text-sm flex items-center gap-1.5"
            data-testid="nav-exercises-btn"
          >
            <Dumbbell className="h-4 w-4" />
            Exercises
          </a>
          <a
            href="/trainers.html"
            className="text-slate-400 font-medium hover:text-[#f1f3fc] transition-colors font-headline tracking-tight text-sm flex items-center gap-1.5"
            data-testid="nav-trainers-btn"
          >
            <Users className="h-4 w-4" />
            Trainers
          </a>
          <a
            href="/blog.html"
            className="text-slate-400 font-medium hover:text-[#f1f3fc] transition-colors font-headline tracking-tight text-sm flex items-center gap-1.5"
            data-testid="nav-blog-btn"
          >
            <BookOpen className="h-4 w-4" />
            Blog
          </a>
          <a
            href="/calculators.html"
            onClick={(e) => { e.preventDefault(); onOpenCalculators(); }}
            className="text-slate-400 font-medium hover:text-[#f1f3fc] transition-colors font-headline tracking-tight text-sm flex items-center gap-1.5"
            data-testid="nav-calculators-btn"
          >
            <Calculator className="h-4 w-4" />
            Calculators
          </a>
          {onOpenFatAI && (
            <a
              href="/fatai.html"
              onClick={(e) => { e.preventDefault(); onOpenFatAI(); }}
              className="relative font-bold hover:opacity-90 transition-opacity font-headline tracking-tight text-sm flex items-center gap-1.5 text-[#00f1fe]"
              data-testid="nav-fatai-btn"
            >
              <Sparkles className="h-4 w-4 text-[#cafd00]" />
              <span><span className="text-[#ff7351]">Fat%</span><span className="text-[#00f1fe]">AI</span></span>
              <span className="ml-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-[#cafd00] text-[#0a0e14] uppercase">Free</span>
            </a>
          )}
        </div>
        <div className="flex items-center gap-3">
          {/* Mobile-only icons: Calculators + Fat%AI (visible <md) */}
          <a
            href="/calculators.html"
            onClick={(e) => { e.preventDefault(); onOpenCalculators(); }}
            className="md:hidden flex items-center justify-center w-9 h-9 rounded-full text-slate-400 hover:text-[#f1f3fc] hover:bg-white/5 transition-colors"
            data-testid="nav-mobile-calculators-btn"
            aria-label="Calculators"
          >
            <Calculator className="h-5 w-5" />
          </a>
          {onOpenFatAI && (
            <a
              href="/fatai.html"
              onClick={(e) => { e.preventDefault(); onOpenFatAI(); }}
              className="md:hidden flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-[#0a0e14] border border-[#00f1fe]/30 text-[11px] font-bold font-headline tracking-tight"
              data-testid="nav-mobile-fatai-btn"
              aria-label="Fat%AI — free body fat scan from photo"
            >
              <Sparkles className="h-3.5 w-3.5 text-[#cafd00]" />
              <span><span className="text-[#ff7351]">Fat%</span><span className="text-[#00f1fe]">AI</span></span>
            </a>
          )}

          {canShowIOSInstall && (
            <button
              onClick={openGuide}
              className="hidden sm:flex items-center gap-1.5 text-[#00f1fe] text-sm font-bold hover:text-[#00f1fe]/80 transition-colors"
            >
              <Download className="h-4 w-4" />
              Install
            </button>
          )}
          <button
            onClick={() => onOpenAuth('login')}
            className="hidden md:block text-[#f1f3fc] px-4 py-2 rounded-full font-bold text-sm hover:text-[#cafd00] transition-colors font-headline"
            data-testid="nav-login-btn"
          >
            Log In
          </button>
        </div>
      </div>
    </nav>
  );
}

export const NavBar = React.memo(NavBarImpl);
